import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Dropdown, Form } from 'react-bootstrap';
import {
  ComboWeatherSnapshot,
  OperatorSpraySessionCombo,
  ProductConsumed,
  SpraySession,
  sprayService,
} from '../../services/sprayService';
import { Field, Modal } from '../patterns';

/**
 * Per-combo manager intervention menu. Wired into `ComboSummaryList`'s
 * `renderRowActions` slot by Task 5.5 for manager surfaces only.
 *
 * Visibility per combo status:
 *   not_started → Cancel combo (uses removeCombo / DELETE)
 *   in_progress → End early + Abandon
 *   completed / incomplete / cancelled → nothing (component renders null)
 *
 * Gating note: this component does NOT re-check
 * `useMenu('spraying:sessions')`. Task 5.5 owns the conditional render and
 * only mounts this inside the manager branch of `SpraySessionDetail`. A
 * pure operator viewing the detail page therefore never sees the trigger.
 *
 * Cancel-combo design choice: the backend has a `manager-cancel`
 * transition on `transitionCombo` but no dedicated route. For
 * "this combo was planned but no work will be done," DELETE is the
 * cleaner shortcut — it removes the planned-but-never-touched row from
 * the session entirely (matching user intent). End-early / abandon are
 * the right tools when work DID start.
 */
export interface ComboActionMenuProps {
  combo: OperatorSpraySessionCombo;
  /**
   * Called after a successful action with the API response. Parent
   * re-renders to reflect new combo + parent state. For removeCombo
   * (which returns void) the menu emits `{}` so the parent can refetch.
   */
  onComboChanged: (next: { combo?: OperatorSpraySessionCombo; parent?: SpraySession }) => void;
}

type ActionKind = 'cancel' | 'endEarly' | 'abandon';

interface EndEarlyState {
  reason: string;
  tankCount: string;
  temperature: string;
  windSpeed: string;
  products: Array<ProductConsumed & { unit?: string }>;
}

interface AbandonState {
  reason: string;
  confirmedNoProduct: boolean;
}

const ComboActionMenu: React.FC<ComboActionMenuProps> = ({ combo, onComboChanged }) => {
  const { t } = useTranslation();

  const [activeAction, setActiveAction] = useState<ActionKind | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [endEarly, setEndEarly] = useState<EndEarlyState>({
    reason: '',
    tankCount: '',
    temperature: '',
    windSpeed: '',
    products: [
      {
        productId: combo.productsConsumed?.[0]?.productId ?? '',
        quantity: 0,
        unit: combo.productsConsumed?.[0]?.unit ?? '',
      },
    ],
  });
  const [abandon, setAbandon] = useState<AbandonState>({
    reason: '',
    confirmedNoProduct: false,
  });

  // Per-status visibility.
  const showCancel = combo.status === 'not_started';
  const showEndEarly = combo.status === 'in_progress';
  const showAbandon = combo.status === 'in_progress';

  // No actions for this combo → render nothing. Keeps the row's action
  // area clean rather than dropping in an empty trigger.
  if (!showCancel && !showEndEarly && !showAbandon) {
    return null;
  }

  const closeModal = () => {
    if (loading) return;
    setActiveAction(null);
    setError('');
  };

  const openAction = (kind: ActionKind) => {
    setError('');
    setActiveAction(kind);
  };

  const handleCancelConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await sprayService.removeCombo(combo._id);
      setLoading(false);
      setActiveAction(null);
      onComboChanged({});
    } catch (err: any) {
      setLoading(false);
      setError(err?.message ?? t('common.error'));
    }
  };

  const endEarlyValid =
    endEarly.reason.trim().length > 0 && Number(endEarly.tankCount) > 0;

  const handleEndEarlyConfirm = async () => {
    if (!endEarlyValid) return;
    setLoading(true);
    setError('');
    try {
      const weatherEnd: ComboWeatherSnapshot = {};
      if (endEarly.temperature.trim() !== '') {
        weatherEnd.temperature = Number(endEarly.temperature);
      }
      if (endEarly.windSpeed.trim() !== '') {
        weatherEnd.windSpeed = Number(endEarly.windSpeed);
      }
      const productsConsumed = endEarly.products
        // Drop rows with no product selected — the backend treats an empty
        // list as "no consumption recorded" which is fine for end-early.
        .filter(p => p.productId)
        .map(p => ({
          productId: p.productId,
          quantity: Number(p.quantity) || 0,
          unit: p.unit ?? '',
        }));
      const result = await sprayService.endEarlyCombo(combo._id, {
        reason: endEarly.reason.trim(),
        productsConsumed,
        tankCount: Number(endEarly.tankCount),
        weatherEnd,
      });
      setLoading(false);
      setActiveAction(null);
      onComboChanged(result);
    } catch (err: any) {
      setLoading(false);
      setError(err?.message ?? t('common.error'));
    }
  };

  const abandonValid =
    abandon.reason.trim().length > 0 && abandon.confirmedNoProduct;

  const handleAbandonConfirm = async () => {
    if (!abandonValid) return;
    setLoading(true);
    setError('');
    try {
      const result = await sprayService.abandonCombo(combo._id, {
        reason: abandon.reason.trim(),
        confirmedNoProductSprayed: true,
      });
      setLoading(false);
      setActiveAction(null);
      onComboChanged(result);
    } catch (err: any) {
      setLoading(false);
      setError(err?.message ?? t('common.error'));
    }
  };

  const updateProductRow = (
    idx: number,
    patch: Partial<ProductConsumed & { unit?: string }>,
  ) => {
    setEndEarly(s => ({
      ...s,
      products: s.products.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    }));
  };

  const addProductRow = () => {
    setEndEarly(s => ({
      ...s,
      products: [...s.products, { productId: '', quantity: 0, unit: '' }],
    }));
  };

  const removeProductRow = (idx: number) => {
    setEndEarly(s => ({
      ...s,
      products: s.products.filter((_, i) => i !== idx),
    }));
  };

  return (
    <>
      <Dropdown align="end">
        <Dropdown.Toggle
          variant="outline-secondary"
          size="sm"
          data-testid={`combo-action-menu-${combo._id}`}
        >
          {t('spray.sessions.comboActions.menu')}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {showCancel && (
            <Dropdown.Item as="button" onClick={() => openAction('cancel')}>
              {t('spray.sessions.comboActions.cancelButton')}
            </Dropdown.Item>
          )}
          {showEndEarly && (
            <Dropdown.Item as="button" onClick={() => openAction('endEarly')}>
              {t('spray.sessions.comboActions.endEarlyButton')}
            </Dropdown.Item>
          )}
          {showAbandon && (
            <Dropdown.Item as="button" onClick={() => openAction('abandon')}>
              {t('spray.sessions.comboActions.abandonButton')}
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {activeAction === 'cancel' && (
        <Modal
          show
          onHide={closeModal}
          title={t('spray.sessions.comboActions.cancelTitle')}
        >
          <Modal.Body>
            <p>{t('spray.sessions.comboActions.cancelBody')}</p>
            {error && (
              <Alert variant="danger" className="mt-3 mb-0">
                {error}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={closeModal}
              disabled={loading}
            >
              {t('spray.sessions.comboActions.closeButton')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--danger"
              onClick={handleCancelConfirm}
              disabled={loading}
            >
              {t('spray.sessions.comboActions.cancelConfirm')}
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {activeAction === 'endEarly' && (
        <Modal
          show
          onHide={closeModal}
          title={t('spray.sessions.comboActions.endEarlyTitle')}
          size="lg"
        >
          <Modal.Body>
            <p>{t('spray.sessions.comboActions.endEarlyBody')}</p>

            <div className="mb-3">
              <Field
                label={t('spray.sessions.comboActions.reasonLabel')}
                controlId="combo-action-end-early-reason"
                helpText={
                  !endEarly.reason.trim()
                    ? t('spray.sessions.comboActions.reasonRequired')
                    : undefined
                }
              >
                <textarea
                  id="combo-action-end-early-reason"
                  className="pat-input"
                  rows={2}
                  value={endEarly.reason}
                  onChange={e =>
                    setEndEarly(s => ({ ...s, reason: e.target.value }))
                  }
                  disabled={loading}
                />
              </Field>
            </div>

            <div className="mb-3">
              <Field
                label={t('spray.sessions.comboActions.tankCountLabel')}
                controlId="combo-action-end-early-tank"
                helpText={
                  Number(endEarly.tankCount) <= 0
                    ? t('spray.sessions.comboActions.tankCountRequired')
                    : undefined
                }
              >
                <input
                  id="combo-action-end-early-tank"
                  type="number"
                  className="pat-input"
                  min={1}
                  value={endEarly.tankCount}
                  onChange={e =>
                    setEndEarly(s => ({ ...s, tankCount: e.target.value }))
                  }
                  disabled={loading}
                />
              </Field>
            </div>

            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <strong>{t('spray.sessions.combos.products')}</strong>
                <button
                  type="button"
                  className="pat-btn pat-btn--subtle"
                  onClick={addProductRow}
                  disabled={loading}
                >
                  {t('spray.sessions.comboActions.addProductRow')}
                </button>
              </div>
              {endEarly.products.map((row, idx) => (
                <div
                  key={idx}
                  className="d-flex gap-2 align-items-end mb-2"
                  data-testid={`end-early-product-row-${idx}`}
                >
                  <div className="flex-grow-1">
                    <Field
                      label={t('spray.sessions.combos.productLabel')}
                      controlId={`combo-action-end-early-product-${idx}`}
                    >
                      <input
                        id={`combo-action-end-early-product-${idx}`}
                        type="text"
                        className="pat-input"
                        value={row.productId}
                        onChange={e =>
                          updateProductRow(idx, { productId: e.target.value })
                        }
                        disabled={loading}
                      />
                    </Field>
                  </div>
                  <Field
                    label={t('spray.sessions.combos.quantityLabel')}
                    controlId={`combo-action-end-early-quantity-${idx}`}
                  >
                    <input
                      id={`combo-action-end-early-quantity-${idx}`}
                      type="number"
                      className="pat-input"
                      min={0}
                      value={row.quantity}
                      onChange={e =>
                        updateProductRow(idx, { quantity: Number(e.target.value) })
                      }
                      disabled={loading}
                    />
                  </Field>
                  <Field
                    label={t('spray.sessions.combos.unitLabel')}
                    controlId={`combo-action-end-early-unit-${idx}`}
                  >
                    <input
                      id={`combo-action-end-early-unit-${idx}`}
                      type="text"
                      className="pat-input"
                      value={row.unit ?? ''}
                      onChange={e => updateProductRow(idx, { unit: e.target.value })}
                      disabled={loading}
                    />
                  </Field>
                  {endEarly.products.length > 1 && (
                    <button
                      type="button"
                      className="pat-btn pat-btn--danger"
                      onClick={() => removeProductRow(idx)}
                      disabled={loading}
                    >
                      {t('spray.sessions.comboActions.removeProductRow')}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="mb-3">
              <strong>{t('spray.sessions.comboActions.weatherEndSectionTitle')}</strong>
              <div className="d-flex gap-2 mt-2">
                <div className="flex-grow-1">
                  <Field
                    label={t('spray.sessions.comboActions.temperatureLabel')}
                    controlId="combo-action-end-early-temperature"
                  >
                    <input
                      id="combo-action-end-early-temperature"
                      type="number"
                      className="pat-input"
                      value={endEarly.temperature}
                      onChange={e =>
                        setEndEarly(s => ({ ...s, temperature: e.target.value }))
                      }
                      disabled={loading}
                    />
                  </Field>
                </div>
                <div className="flex-grow-1">
                  <Field
                    label={t('spray.sessions.comboActions.windSpeedLabel')}
                    controlId="combo-action-end-early-wind"
                  >
                    <input
                      id="combo-action-end-early-wind"
                      type="number"
                      className="pat-input"
                      value={endEarly.windSpeed}
                      onChange={e =>
                        setEndEarly(s => ({ ...s, windSpeed: e.target.value }))
                      }
                      disabled={loading}
                    />
                  </Field>
                </div>
              </div>
            </div>

            {error && (
              <Alert variant="danger" className="mt-3 mb-0">
                {error}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={closeModal}
              disabled={loading}
            >
              {t('spray.sessions.comboActions.closeButton')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={handleEndEarlyConfirm}
              disabled={loading || !endEarlyValid}
            >
              {t('spray.sessions.comboActions.endEarlyConfirm')}
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {activeAction === 'abandon' && (
        <Modal
          show
          onHide={closeModal}
          title={t('spray.sessions.comboActions.abandonTitle')}
        >
          <Modal.Body>
            <p>{t('spray.sessions.comboActions.abandonBody')}</p>

            <div className="mb-3">
              <Field
                label={t('spray.sessions.comboActions.reasonLabel')}
                controlId="combo-action-abandon-reason"
                helpText={
                  !abandon.reason.trim()
                    ? t('spray.sessions.comboActions.reasonRequired')
                    : undefined
                }
              >
                <textarea
                  id="combo-action-abandon-reason"
                  className="pat-input"
                  rows={3}
                  value={abandon.reason}
                  onChange={e => setAbandon(s => ({ ...s, reason: e.target.value }))}
                  disabled={loading}
                />
              </Field>
            </div>

            <div className="mb-0">
              <Form.Check
                id="combo-action-abandon-confirm"
                type="checkbox"
                checked={abandon.confirmedNoProduct}
                onChange={e =>
                  setAbandon(s => ({ ...s, confirmedNoProduct: e.target.checked }))
                }
                disabled={loading}
                label={t('spray.sessions.comboActions.confirmNoProductLabel')}
              />
              {!abandon.confirmedNoProduct && (
                <div className="pat-field__help">
                  {t('spray.sessions.comboActions.confirmNoProductRequired')}
                </div>
              )}
            </div>

            {error && (
              <Alert variant="danger" className="mt-3 mb-0">
                {error}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={closeModal}
              disabled={loading}
            >
              {t('spray.sessions.comboActions.closeButton')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--danger"
              onClick={handleAbandonConfirm}
              disabled={loading || !abandonValid}
            >
              {t('spray.sessions.comboActions.abandonConfirm')}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default ComboActionMenu;
